<template>
  <button
    class="hidden lg:inline-flex items-center gap-x-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all"
    :class="[statusClass]"
    @click="goToStatus"
  >
    <component
      :is="statusIcon"
      class="h-5 w-5"
      :class="{ 'animate-spin': status === 'loading' }"
    />
    <span>{{ statusText }}</span>
  </button>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  WrenchIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/outline";

// 定义状态类型
type SystemStatus =
  | "all_operational"
  | "partial_down"
  | "majority_down"
  | "all_down"
  | "maintenance"
  | "unknown"
  | "loading";

const { t } = useI18n();
const router = useRouter();
const status = ref<SystemStatus>("unknown");

// 获取系统状态
const fetchStatus = async () => {
  try {
    // 使用代理路径
    const response = await fetch("/status/api/status-page/heartbeat/abdnims");

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const heartbeatList = data.heartbeatList;

    // 计算系统状态
    const statuses = Object.keys(heartbeatList).map((id) => {
      const beats = heartbeatList[id];
      return beats.length > 0 ? beats[beats.length - 1].status : 2;
    });

    if (statuses.length === 0) {
      status.value = "unknown";
      return;
    }

    // 检查是否所有系统都在线
    if (statuses.every((s) => s === 1)) {
      status.value = "all_operational";
      return;
    }

    // 检查是否全部系统离线
    if (statuses.every((s) => s === 0)) {
      status.value = "all_down";
      return;
    }

    // 检查是否有系统处于维护状态
    if (statuses.some((s) => s === 3)) {
      status.value = "maintenance";
      return;
    }

    // 计算离线系统数量
    const downCount = statuses.filter((s) => s === 0).length;
    const totalCount = statuses.length;

    // 检查是否过半数系统离线
    if (downCount > totalCount / 2) {
      status.value = "majority_down";
      return;
    }

    // 检查是否有一个或多个系统离线
    if (downCount > 0) {
      status.value = "partial_down";
      return;
    }

    status.value = "unknown";
  } catch (error) {
    console.error("Error fetching status:", error);
    status.value = "unknown";
  }
};

// 状态图标
const statusIcon = computed(() => {
  switch (status.value) {
    case "all_operational":
      return CheckCircleIcon;
    case "partial_down":
      return ExclamationTriangleIcon;
    case "majority_down":
      return ExclamationCircleIcon;
    case "all_down":
      return XCircleIcon;
    case "maintenance":
      return WrenchIcon;
    case "loading":
      return ArrowPathIcon;
    default:
      return QuestionMarkCircleIcon;
  }
});

// 状态文本
const statusText = computed(() => {
  switch (status.value) {
    case "all_operational":
      return t("status.allOperational");
    case "partial_down":
      return t("status.partialDown");
    case "majority_down":
      return t("status.majorityDown");
    case "all_down":
      return t("status.allDown");
    case "maintenance":
      return t("status.maintenance");
    default:
      return t("status.unknown");
  }
});

// 状态类名
const statusClass = computed(() => {
  const baseClasses: Record<SystemStatus, string> = {
    all_operational:
      "bg-green-50 text-green-600 ring-1 ring-green-500/10 hover:bg-green-100 hover:ring-green-500/20",
    partial_down:
      "bg-yellow-50 text-yellow-600 ring-1 ring-yellow-500/10 hover:bg-yellow-100 hover:ring-yellow-500/20",
    majority_down:
      "bg-yellow-50 text-yellow-600 ring-1 ring-yellow-500/10 hover:bg-yellow-100 hover:ring-yellow-500/20",
    all_down:
      "bg-red-50 text-red-600 ring-1 ring-red-500/10 hover:bg-red-100 hover:ring-red-500/20",
    maintenance:
      "bg-blue-50 text-blue-600 ring-1 ring-blue-500/10 hover:bg-blue-100 hover:ring-blue-500/20",
    unknown:
      "bg-gray-50 text-gray-600 ring-1 ring-gray-200/20 hover:bg-gray-100 hover:ring-gray-300/30",
    loading:
      "bg-gray-50 text-gray-600 ring-1 ring-gray-200/20 hover:bg-gray-100 hover:ring-gray-300/30",
  };

  return baseClasses[status.value];
});

// 跳转到状态页面
const goToStatus = () => {
  router.push("/status");
};

let refreshInterval: ReturnType<typeof setInterval>;

onMounted(() => {
  status.value = "loading";
  fetchStatus();
  // 每5秒更新一次状态
  refreshInterval = setInterval(fetchStatus, 5000);
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});
</script>

<style scoped>
/* 强制指定颜色，确保不受暗色模式影响 */
button {
  background-color: inherit !important;
  border: 1px solid transparent !important;
}

/* 绿色状态 - 正常运行 */
.bg-green-50 {
  background-color: #f0fdf4 !important;
}

.text-green-600 {
  color: #16a34a !important;
}

.ring-green-500\/10 {
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.1) !important;
}

.hover\:bg-green-100:hover {
  background-color: #dcfce7 !important;
}

.hover\:ring-green-500\/20:hover {
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.2) !important;
}

/* 黄色状态 - 部分故障 */
.bg-yellow-50 {
  background-color: #fefce8 !important;
}

.text-yellow-600 {
  color: #ca8a04 !important;
}

.ring-yellow-500\/10 {
  box-shadow: 0 0 0 1px rgba(234, 179, 8, 0.1) !important;
}

.hover\:bg-yellow-100:hover {
  background-color: #fef3c7 !important;
}

.hover\:ring-yellow-500\/20:hover {
  box-shadow: 0 0 0 1px rgba(234, 179, 8, 0.2) !important;
}

/* 红色状态 - 故障 */
.bg-red-50 {
  background-color: #fef2f2 !important;
}

.text-red-600 {
  color: #dc2626 !important;
}

.ring-red-500\/10 {
  box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.1) !important;
}

.hover\:bg-red-100:hover {
  background-color: #fee2e2 !important;
}

.hover\:ring-red-500\/20:hover {
  box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.2) !important;
}

/* 蓝色状态 - 维护 */
.bg-blue-50 {
  background-color: #eff6ff !important;
}

.text-blue-600 {
  color: #2563eb !important;
}

.ring-blue-500\/10 {
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.1) !important;
}

.hover\:bg-blue-100:hover {
  background-color: #dbeafe !important;
}

.hover\:ring-blue-500\/20:hover {
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2) !important;
}

/* 灰色状态 - 未知 */
.bg-gray-50 {
  background-color: #f9fafb !important;
}

.text-gray-600 {
  color: #4b5563 !important;
}

.ring-gray-200\/20 {
  box-shadow: 0 0 0 1px rgba(229, 231, 235, 0.2) !important;
}

.hover\:bg-gray-100:hover {
  background-color: #f3f4f6 !important;
}

.hover\:ring-gray-300\/30:hover {
  box-shadow: 0 0 0 1px rgba(209, 213, 219, 0.3) !important;
}
</style>
